<template>
  <LKProgressCircular
    v-if="isLoadingAsyncData"
    overlay
    :size="50"
    style="align-items: start; padding-top: 30vh;"
  />

  <LKPublicationsFormdataForm
    v-else
    ref="FormdataForm"
    :form-template-id="formTemplate.id"
    :form-data-id="form_data_id"
    :data-collection-id="data_collection_id"
    :publication-id="publicationId"
    :procedure-digit-code="single.procedure_digit_code"
    :name="formTemplate.name"
    :sheets="sheets"
    :loading="isLoading"
    :loading-closing="isLoadingClosing"
    :data-collection="dataCollectionLocal"
    :data-prefilled="prefilledData"
    :data-collection-empty="isEmpty"
    :can-edit="!isClose"
    :intermediate="intermediate"
    :close="isClose"
    :readonly="isReadOnly"
    :submitted="isSubmitted"
    :use-excel="useExcel"
    @draft:clear="onReset"
    @draft:save="onSaveDraft"
    @input="onInput"
    @close="onCancel"
    @submit="onSubmit"
    @reload="reloadData"
  />
</template>

<script>
import { StorageService } from '@/helpers/storage.service'
import { FormdataStatuses } from '@/typings/api/Formdata'
import { LK_PUBLICATIONS_ROUTES as ROUTES } from '../utils'
import { lkFormdataMapper as StoreMapper } from './store'
import { createSheetData } from './createSheetData'

import LKProgressCircular from '@/components/lk/LKProgressCircular.vue'
import LKPublicationsFormdataForm from './components/LKPublicationsFormdataForm.vue'


const DRAFT_VER = 5

export const getKey = (data_collection_id, form_template_id, form_data_id) => {
  const storeKeys = {
    collection: data_collection_id,
    form_template: form_template_id,
    form_data_id,
  }

  const id = Object.keys(storeKeys).reduce((acc, key) => {
    if (storeKeys[key]) acc.push(`${key}/${storeKeys[key]}`)
    return acc
  }, []).join('/')

  return id
}

export const saveDraft = (data_collection_id, form_template_id, form_data_id, dataCollection) => {
  const data = {
    dataCollection,
    data_collection_id,
    form_template_id,
    form_data_id,
    ver: DRAFT_VER,
  }

  const id = getKey(data_collection_id, form_template_id, form_data_id)

  StorageService.formDataDraft.set(data, id)
}

export const getDraft = (data_collection_id, form_template_id, form_data_id) => {
  const id = getKey(data_collection_id, form_template_id, form_data_id)
  const data = StorageService.formDataDraft.get(id)
  return data?.ver < DRAFT_VER ? void 0 : data?.dataCollection
}

export default {
  name: 'LKPublicationsFormdataView',
  components: {
    LKProgressCircular,
    LKPublicationsFormdataForm,
  },
  inheritAttrs: false,
  props: {
    slug: String,
    data_collection_id: {
      type: [String, Number],
      required: true,
    },
    form_template_id: {
      type: [String, Number],
      required: true,
    },
    form_data_id: {
      type: [String, Number],
    },
  },
  data: () => ({
    isLoading: false,
    isLoadingAsyncData: false,
    isLoadingClosing: false,
    withDraft: false,
    dataCollectionLocal: [],
    prefilledData: [],
  }),
  computed: {
    ...StoreMapper.mapState([
      'formTemplate',
      'single',
    ]),
    isDetachedPage() {
      return this.$route.path?.includes('/detach/')
    },
    sheets() {
      return this.formTemplate.sheets
        .filter((_) => _.cells.length)
    },
    isClose() {
      return this.single?.status === FormdataStatuses.close
    },
    isReadOnly() {
      if (this.single.status === FormdataStatuses.submitted && this.single.data_collection.data_collection_closed) {
        return true
      }
      return [
        FormdataStatuses.overdue,
        FormdataStatuses.archive,
        FormdataStatuses.close,
        FormdataStatuses.prefilled,
      ].includes(this.single?.status)
    },
    isSubmitted() {
      return this.single?.is_submitted
    },
    intermediate() {
      return this.single.data_collection.collect_intermediate_revisions
    },
    useExcel() {
      return this.single.data_collection.use_excel
    },
    isEmpty() {
      return !this.single.data
        || this.single.status === FormdataStatuses.prefill
        || Object.keys(this.single.data).length === 0
    },
    publicationId() {
      return this.single?.data_collection?.publication_id
    },
  },
  mounted() {
    const { query } = this.$route
    if (query.success) {
      this.$notice.create({
        type: 'success',
        timeout: 6000,
        text: 'Форма успешно сдана',
      })

      this.$nextTick(() => {
        delete query.success

        this.$router.push({
          query: {
            ...query,
            view: true,
          },
        })
      })
    }
  },
  methods: {
    ...StoreMapper.mapActions([
      'getFormTemplate',
      'getOne',
      'update',
      'updateDraft',
      'create',
      'opened',
      'submit',
    ]),

    async asyncData() {
      this.isLoadingAsyncData = true

      if (!this.form_data_id) {
        const payload = {
          data: {},
          data_collection_id: +this.data_collection_id,
          form_template_id: +this.form_template_id,
        }

        const { id } = await this.create({ payload })

        const location = {
          params: {
            ...this.$route.params,
            form_data_id: id,
          },
        }

        await this.$router.replace(location)
      }

      const promises = [
        this.getFormTemplate(this.form_template_id),
        this.getOne({ id: this.form_data_id }),
      ]

      try {
        await Promise.all(promises)
        // this.opened({ id: this.form_data_id })
        this.onSetDataCollection()
        this.onSetPrefilledData()
      } catch (e) {
        if (e.status === 403) {
          await this.$router.push({ name: ROUTES.LIST })
        }
        this.$notice.errors(e)
      }

      this.isLoadingAsyncData = false
    },
    async onSubmit() {
      this.isLoading = true

      const { intermediate } = this
      const data = this.dataCollectionLocal

      if (intermediate) {
        const date = new Date().toISOString()

        data.forEach((dataCollection) => {
          dataCollection.forEach((line) => {
            // eslint-disable-next-line no-param-reassign
            line.$readonly = line.$readonly || date
            return data
          })
        })
      }

      const payload = {
        data,
        id: this.single.id,
        data_collection: void 0,
        data_collection_id: +this.data_collection_id,
        form_template_id: +this.form_template_id,
      }

      try {
        await this.update({ payload, id: this.form_data_id })
        clearInterval(this.intervalId)
      } catch (e) {
        this.$notice.errors(e)
      }

      try {
        await this.submit({ id: this.form_data_id, close: !intermediate })
        this.stop = true
        await this.closeAfterSubmit()
      } catch (e) {
        if (!e.data?.message) {
          const BASE_MSG = 'Обнаружены ошибки в отчёте'
          let message = BASE_MSG
          if (Array.isArray(e.errors)) {
            const errorsCount = (e.errors.map((list) => list.length || 0)).reduce((acc, cur) => acc + cur)
            message = `${BASE_MSG} (${errorsCount})`
            await this.$refs.FormdataForm.validateUsingBackendApi(e.errors)
          } else if (typeof e.errors === 'string') {
            message = e.errors
          }

          this.$notice.errors(e, {
            message,
            isUnknownErrorStructure: true,
          })
        } else {
          this.$notice.errors(e)
        }
        this.isLoading = false
        await this.$refs.FormdataForm.onShowErrors()
      }

      this.isLoading = false
    },
    async reloadData() {
      this.isLoading = true
      try {
        await this.getOne({ id: this.form_data_id })
        this.onSetDataCollection()
      } catch (e) {
        this.$notice.errors(e)
      } finally {
        this.isLoading = false
      }
    },
    onSetPrefilledData() {
      if (this.single?.prefill_data?.length) {
        this.prefilledData = this.single?.prefill_data
      }
    },
    onSetDataCollection() {
      // const single = getDraft(
      //   this.data_collection_id,
      //   this.form_template_id,
      // )
      const single = null

      if (single?.length) {
        this.dataCollectionLocal = single
        this.withDraft = !this.isEqual(this.dataCollectionLocal)
        return
      }

      if (this.single?.data?.length) {
        this.dataCollectionLocal = (
          JSON.parse(JSON.stringify(this.filterData(this.single.data)))
        )
      }

      this.sheets.forEach(({ cells }, i) => {
        if (!this.dataCollectionLocal[i]?.length) {
          this.$set(this.dataCollectionLocal, i, createSheetData(cells))
        }
      })

      this.dataCollectionPrevString = JSON.stringify(this.dataCollectionLocal)
    },
    onReset() {
      const id = this.form_template_id
      StorageService.formDataDraft.remove(id)
      this.withDraft = false
      this.onSetDataCollection()
    },
    isEqual(dataCollectionLocal) {
      return (
        JSON.stringify(dataCollectionLocal)
        === this.dataCollectionPrevString
      )
    },
    async onSaveDraft(force = false) {
      if (!force) {
        if (this.isLoading) return

        this.withDraft = !this.isEqual(this.dataCollectionLocal)

        if (!this.withDraft) return
      }

      const payload = { data: this.dataCollectionLocal }

      try {
        await this.updateDraft({ ...payload, id: this.form_data_id })
        this.dataCollectionPrevString = JSON.stringify(payload.data)
        this.withDraft = false

        // await this.$refs.FormdataForm.validateUsingBackendApi()
      } catch (error) {
        this.$notice.errors(error)
        saveDraft(
          this.data_collection_id,
          this.form_template_id,
          null,
          this.dataCollectionLocal,
        )
      }
    },
    async closeAfterSubmit() {
      if (this.isDetachedPage) {
        this.$router.push({
          query: {
            success: true,
          },
        })
        this.$router.go()
      } else {
        const location = this.slug
          ? {
            name: ROUTES.VIEW,
            params: {
              topic_slug: this.$route.params.topic_slug,
              slug: this.slug,
            },
          }
          : {
            name: ROUTES.LIST,
            params: { topic_slug: this.$route.params.topic_slug },
          }
        await this.$router.push(location)
      }
    },
    async onCancel({ skipSave } = {}) {
      this.isLoadingClosing = true
      const location = this.slug
        ? {
          name: ROUTES.VIEW,
          params: {
            topic_slug: this.$route.params.topic_slug,
            slug: this.slug,
          },
        }
        : {
          name: ROUTES.LIST,
          params: { topic_slug: this.$route.params.topic_slug },
        }

      if (!this.isClose && !skipSave) {
        await this.onSaveDraft(true)
      }
      await this.$router.push(location)
    },
    onInput(data) {
      this.dataCollectionLocal = data

      this.sheets.forEach(({ cells }, i) => {
        if (!this.dataCollectionLocal[i]?.length) {
          this.$set(this.dataCollectionLocal, i, createSheetData(cells))
        }
      })

      this.dataCollectionPrevString = JSON.stringify(this.dataCollectionLocal)
    },
    filterData(singleData) {
      const newData = []
      singleData.forEach((dataCollection) => {
        const filteredRows = (dataCollection || []).filter(Boolean)
        newData.push(filteredRows)
      })
      return newData
    },
  },
}
</script>
